<template>
  <div>
    <div class="login-container">
      <v-card
        class="login-card elevation-24"
        :style="{ background: getThemeColor }"
        rounded="lg"
      >
        <!-- Progress Indicator -->
        <div class="progress-container">
          <v-progress-linear
            :value="progressValue"
            height="4"
            color="white"
            background-color="rgba(255,255,255,0.3)"
            class="mb-0"
          ></v-progress-linear>
        </div>

        <v-card-text class="pa-8">
          <!-- Header with Logo-->
          <div class="text-center mb-6">
            <div>
              <v-avatar size="80" class="mb-4 elevation-8">
                <v-img
                  v-if="schoolLogo"
                  contain
                  max-height="100%"
                  :src="schoolLogo"
                ></v-img>
                <v-icon size="40" color="white" v-else
                  >mdi-account-circle</v-icon
                >
              </v-avatar>
              <h1 class="display-1 font-weight-light text-white mb-2">
                {{ userCompany?.name || "Welcome Back" }}
              </h1>
            </div>
            <p class="subtitle-1 text-white opacity-80">
              {{ stepTitle }}
            </p>
          </div>

          <!-- Step Content with Transitions -->
          <transition :name="transitionName" mode="out-in">
            <!-- Step 1: Email Entry -->
            <div v-if="currentStep === 1" key="step1" class="step-content">
              <v-form ref="emailForm" v-model="emailValid">
                <v-text-field
                  dark
                  v-model="userEmail"
                  :rules="emailRules"
                  label="Email Address"
                  placeholder="Enter your email address"
                  prepend-inner-icon="mdi-email"
                  variant="outlined"
                  color="white"
                  class="custom-input mb-4"
                  :loading="loading"
                  @keyup.enter="handleEmailSubmit"
                ></v-text-field>

                <div class="text-center">
                  <v-btn
                    @click="handleEmailSubmit"
                    :loading="loading"
                    :disabled="!emailValid || loading"
                    outlined
                    class="text-white opacity-80 mb-6"
                    size="x-large"
                  >
                    Continue
                  </v-btn>
                </div>

                <div class="text-center">
                  <v-btn
                    @click="initiatePhoneChange"
                    text
                    size="x-small"
                    elevation="8"
                  >
                    Change Phone Number
                  </v-btn>
                </div>
              </v-form>
            </div>

            <!-- Step 2: Phone Verification/Update -->
            <div v-else-if="currentStep === 2" key="step2" class="step-content">
              <div class="text-center mb-6">
                <v-icon size="64" color="white" class="mb-4">mdi-phone</v-icon>
                <p class="text-white opacity-90">
                  {{
                    phoneVerified
                      ? "We sent a verification code to your phone"
                      : "Please confirm or update your phone number"
                  }}
                </p>
              </div>

              <!-- Phone Update Form -->
              <div v-if="!phoneVerified">
                <v-form ref="phoneForm" v-model="phoneValid">
                  <v-text-field
                    dark
                    v-model="userPhone"
                    :rules="phoneRules"
                    label="Phone Number"
                    placeholder="Enter your phone number"
                    prepend-inner-icon="mdi-phone"
                    variant="outlined"
                    color="white"
                    class="custom-input mb-4"
                    :loading="loading"
                    @keyup.enter="updatePhoneNumber"
                  ></v-text-field>

                  <v-text-field
                    dark
                    v-model="userPassword"
                    :rules="passwordRules"
                    label="Password"
                    placeholder="Password"
                    :append-icon="showPass ? 'mdi-eye' : 'mdi-eye-off'"
                    :type="showPass ? 'text' : 'password'"
                    prepend-inner-icon="mdi-lock"
                    hint="At least 8 characters"
                    counter
                    variant="outlined"
                    color="white"
                    class="custom-input mb-4"
                    :loading="loading"
                    @keyup.enter="updatePhoneNumber"
                    @click:append="showPass = !showPass"
                  ></v-text-field>

                  <v-btn
                    @click="updatePhoneNumber"
                    :loading="loading"
                    :disabled="!phoneValid || loading"
                    block
                    size="large"
                    class="custom-btn mb-4"
                    elevation="8"
                  >
                    Update Phone Number
                  </v-btn>
                </v-form>
              </div>

              <!-- OTP Input -->
              <div v-else>
                <div class="text-center mb-6">
                  <p
                    class="text-white opacity-90"
                    v-html="showSMSCreditsOnPage()"
                  ></p>
                </div>

                <v-otp-input
                  v-model="otpCode"
                  length="6"
                  variant="outlined"
                  class="custom-otp mb-6"
                  @finish="handleOTPSubmit"
                ></v-otp-input>

                <v-row class="mb-4">
                  <v-col :cols="6"
                    ><v-btn
                      @click="goBack"
                      block
                      dark
                      color="red"
                      size="large"
                      style="border-radius: 12px"
                    >
                      <v-icon left dark>mdi-arrow-left</v-icon>Go Back
                    </v-btn></v-col
                  >
                  <v-col :cols="6"
                    ><v-btn
                      block
                      @click="handleOTPSubmit"
                      :loading="loading"
                      :disabled="otpCode.length !== 6 || loading"
                      dark
                      size="large"
                      class="custom-btn"
                      elevation="8"
                    >
                      Verify & Login<v-icon left dark>mdi-arrow-right</v-icon>
                    </v-btn></v-col
                  >
                </v-row>

                <div class="text-center">
                  <v-btn
                    @click="resendOTP"
                    :disabled="resendTimer > 0"
                    text
                    class="text-white opacity-80"
                    size="small"
                  >
                    {{
                      resendTimer > 0
                        ? `Resend in ${resendTimer}s`
                        : "Resend Code"
                    }}
                  </v-btn>
                </div>
              </div>
            </div>

            <!-- Step 3: Change Phone Email Entry -->
            <div v-else-if="currentStep === 3" key="step3" class="step-content">
              <div class="text-center mb-6">
                <v-icon size="64" color="white" class="mb-4"
                  >mdi-email-edit</v-icon
                >
                <p class="text-white opacity-90">
                  Enter your registered email to receive a verification link
                </p>
              </div>

              <v-form ref="changeEmailForm" v-model="changeEmailValid">
                <v-text-field
                  dark
                  v-model="changeEmail"
                  :rules="emailRules"
                  label="Email Address"
                  placeholder="Enter your registered email"
                  prepend-inner-icon="mdi-email"
                  variant="outlined"
                  color="white"
                  class="custom-input mb-4"
                  :loading="loading"
                  @keyup.enter="requestPhoneChange"
                ></v-text-field>

                <v-btn
                  @click="requestPhoneChange"
                  :loading="loading"
                  :disabled="!changeEmailValid || loading"
                  block
                  size="large"
                  class="custom-btn mb-4"
                  elevation="8"
                >
                  Send Verification Link
                </v-btn>
              </v-form>

              <div class="text-center">
                <v-btn
                  @click="goBack"
                  text
                  class="text-white opacity-80"
                  size="small"
                  prepend-icon="mdi-arrow-left"
                >
                  Back to Login
                </v-btn>
              </div>
            </div>

            <!-- Step 4: New Phone Number Entry -->
            <div v-else-if="currentStep === 4" key="step4" class="step-content">
              <div class="text-center mb-6">
                <v-icon size="64" color="white" class="mb-4"
                  >mdi-phone-plus</v-icon
                >
                <p class="text-white opacity-90">Enter your new phone number</p>
              </div>

              <v-form ref="newPhoneForm" v-model="newPhoneValid">
                <v-text-field
                  dark
                  v-model="newPhoneNumber"
                  :rules="phoneRules"
                  label="New Phone Number"
                  placeholder="Enter your new phone number"
                  prepend-inner-icon="mdi-phone"
                  variant="outlined"
                  color="white"
                  class="custom-input mb-4"
                  :loading="loading"
                  @keyup.enter="saveNewPhoneNumber"
                ></v-text-field>

                <v-btn
                  @click="saveNewPhoneNumber"
                  :loading="loading"
                  :disabled="!newPhoneValid || loading"
                  block
                  size="large"
                  class="custom-btn mb-4"
                  elevation="8"
                >
                  Save Phone Number
                </v-btn>
              </v-form>
            </div>

            <!-- Step 5: New School Entry -->
            <div v-else-if="currentStep === 5" key="step5" class="step-content">
              <div class="text-center mb-6">
                <AddNewCompanyNow
                  @cancel-creation="goToStep(1)"
                  @company-created="companyCreated()"
                ></AddNewCompanyNow>
              </div>
            </div>
          </transition>

          <!-- Registration Link -->
          <div v-if="currentStep === 1" class="text-center mt-n2">
            <!-- <v-row>
              <v-col :cols="4"
                ><v-btn
                  outlined
                  dark
                  small
                  @click="goTo('requests-and-tickets')"
                  >Enquiries</v-btn
                ></v-col
              >
              <v-col :cols="isMobile ? 3 : 4"></v-col>
              <v-col :cols="4"
                ><v-btn
                  outlined
                  dark
                  small
                  @click="goTo('requests-and-tickets')"
                  >Contact Us</v-btn
                ></v-col
              >
            </v-row> -->
            <p class="text-white opacity-80 mt-2">
              Don't have an account?
              <v-btn
                @click="$router.push({ path: '/register' })"
                text
                class="text-white pa-0"
                style="text-decoration: underline"
              >
                Register here
              </v-btn>
            </p>
          </div>
        </v-card-text>
      </v-card>

      <!-- Add New School Dialog -->
      <AddNewCompanyDialog
        :dialog="addNewCompanyDialog"
        @close-dialog="addNewCompanyDialog = false"
        @add-company="showAddNewCompanyForm()"
        @remind-later="remindMeLater()"
      ></AddNewCompanyDialog>

      <!-- Success Animation Overlay -->
      <v-overlay
        v-model="showSuccessOverlay"
        class="d-flex align-center justify-center"
      >
        <div class="text-center">
          <v-icon size="80" color="success" class="mb-4 animate-bounce">
            mdi-check-circle
          </v-icon>
          <h2 class="text-white">Welcome back!</h2>
          <p class="text-white opacity-80">Redirecting you now...</p>
        </div>
      </v-overlay>
    </div>
    <vLoader></vLoader>
    <!-- <WebsiteFooter></WebsiteFooter> -->
  </div>
</template>

<script>
import axios from "@/plugins/axios.js";
import helpersJs from "@/plugins/helpers";
import companiesJs from "@/plugins/companies";
import smsJs from "@/plugins/sms.js";

import AddNewCompanyDialog from "@/modules/Companies/AddNewCompany/AddNewCompanyDialog.vue";
import AddNewCompanyNow from "@/modules/Companies/AddNewCompany/AddNewCompanyNow.vue";
import vLoader from "@/components/Vuetify/Loader.vue";

export default {
  name: "ModernLogin",
  components: {
    AddNewCompanyDialog,
    AddNewCompanyNow,
    vLoader,
  },
  props: {
    slug: {
      type: String,
      default: "",
    },
  },
  watch: {
    slug(newVal) {
      if (newVal) this.determineAppState();
    },
  },
  // async created(){
  //   const data = await this.$store.dispatch('setCompanyContext', {company: null, fresh:true});
  //   this.userCompany = data.targetCompany
  // },
  async mounted() {
    this.$store.commit("setScreenWidth", this.$vuetify.breakpoint.width);
    await this.determineAppState();
    this.restoreProgress();
    this.handleQueryParams();
  },
  updated() {
    this.$store.commit("setScreenWidth", this.$vuetify.breakpoint.width);
  },
  computed: {
    getApiUrl() {
      return this.$store.getters.getApiUrl;
    },
    getUserCompany() {
      return this.$store.getters.getUserCompany;
    },
    getThemeColor() {
      return this.$store.getters.getThemeColor;
    },
    isMobile() {
      return this.$store.getters.isMobile;
    },
    getCurrentUser() {
      return this.$store.getters.getCurrentUser;
    },
    progressValue() {
      return (this.currentStep / 4) * 100;
    },
    stepTitle() {
      const titles = {
        1: "Sign in to your account",
        2: this.phoneVerified
          ? "Enter verification code"
          : "Verify your phone number",
        3: "Change phone number",
        4: "Enter new phone number",
      };
      return titles[this.currentStep] || "";
    },
    transitionName() {
      return this.transitionDirection === "forward"
        ? "slide-left"
        : "slide-right";
    },
  },

  data() {
    return {
      appName: null,
      addNewCompanyDialog: false,
      userCompany: null,
      schoolLogo: null,
      logos: [],
      level: null,
      levelID: null,
      smsCredits: 0,
      smsCreditsType: null,

      // Step management
      currentStep: 1,
      transitionDirection: "forward",

      // Form validation
      emailValid: false,
      phoneValid: false,
      changeEmailValid: false,
      newPhoneValid: false,

      // Form data
      userEmail: "",
      userPassword: "",
      userPhone: "",
      changeEmail: "",
      newPhoneNumber: "",
      otpCode: "",

      // User data
      userDetails: null,
      phoneVerified: false,

      // UI states
      loading: false,
      showSuccessOverlay: false,
      resendTimer: 0,

      // Validation rules
      showPass: false,
      passwordRules: [
        (value) => !!value || "Required.",
        (v) => v.length >= 8 || "Min 8 characters",
      ],
      emailRules: [
        (v) => !!v || "Email is required",
        (v) => /.+@.+\..+/.test(v) || "Email must be valid",
      ],
      phoneRules: [
        (v) => !!v || "Phone number is required",
        (v) => /^\+?[\d\s\-()]{10,}$/.test(v) || "Phone number must be valid",
      ],
    };
  },

  methods: {
    async determineAppState() {
      const domain = window.location.hostname;

      if (this.slug) {
        const res = await companiesJs.getCompanyBySlug(this.slug);
        if (
          res &&
          res.status === "success" &&
          (await this.setupCompanyData(res.company))
        ) {
          return;
        }
      }

      if (domain.includes("legalcasebilling")) {
        this.appName = "Legal Case Billing";
      } else if (domain.includes("bitsinvoice")) {
        this.appName = "BitsInvoice";
      } else {
        await this.getCompanyWithDomain(domain);
      }
    },
    async setupCompanyData(compData) {
      const company = compData?.company || null;
      if (!company) return false;

      this.userCompany = company;
      this.appName = company?.name || "";
      helpersJs.initialiseOrUpdateCompanyDataInStorage(null, compData);

      if (company.emis) {
        const logos = await companiesJs.getLogos(company.emis);
        if (logos?.length > 0) {
          this.logo = logos[0];
        }
      }
      return true;
    },
    async getCompanyWithDomain(domain) {
      const compData = await companiesJs.getCompanyWithDomain(domain);
      await this.setupCompanyData(compData);
    },
    showAddNewCompanyForm() {
      this.addNewCompanyDialog = false;
      this.goToStep(5);
    },
    remindMeLater() {
      this.addNewCompanyDialog = false;
      this.goToStep(1);
    },
    companyCreated() {
      this.addNewCompanyDialog = false;
      this.goToStep(1);
    },

    goTo(route) {
      this.$router.push({ name: route });
    },
    getSchoolLogo(logo) {
      this.schoolLogo = logo;
    },
    async getSMSCreditsNoToken() {
      if (this.getUserCompany && this.getUserCompany.emis) {
        this.$store.commit("setLoader", true);
        const response = await smsJs.getSMSCreditsNoToken(
          this.getUserCompany.emis
        );
        this.smsCredits = response.sms_credits;
        this.smsCreditsType = response.sms_credits_type;
        this.$store.commit("setLoader", false);
      }
    },
    showSMSCreditsOnPage() {
      return ``;
      //return (`You have less then <strong>25 free SMSes</strong> left`);
      // return this.smsCredits > 0 &&
      //   this.smsCreditsType &&
      //   this.smsCreditsType.includes("rial")
      //   ? `<strong>Balance Of ${this.smsCredits} Free SMSes Left </strong><br>.
      //   <br>Once Finished, <strong>Paid SMS Credits</strong> Will
      //             Be Loaded Automatically OR Admin Can Buy OR Allocate SMS
      //             Credits On the Switchboard and Pay Later`
      //   : "<strong>SMS Credits Balance Of " +
      //       this.smsCredits +
      //       " SMSes Left</strong>";
    },
    // Step navigation
    goToStep(step) {
      this.transitionDirection =
        step > this.currentStep ? "forward" : "backward";
      this.currentStep = step;
      if (step == 5) {
        this.addNewCompanyDialog = true;
      }
      this.saveProgress();
    },

    goBack() {
      if (this.currentStep > 1) {
        this.goToStep(this.currentStep - 1);
      }
    },

    // Progress management
    saveProgress() {
      const progress = {
        step: this.currentStep,
        email: this.userEmail,
        password: this.userPassword,
        phone: this.userPhone,
        userDetails: this.userDetails,
        phoneVerified: this.phoneVerified,
      };
      localStorage.setItem("login_progress", JSON.stringify(progress));
    },

    restoreProgress() {
      const saved = localStorage.getItem("login_progress");
      if (saved) {
        try {
          const progress = JSON.parse(saved);
          this.currentStep = progress.step || 1;
          this.userEmail = progress.email || "";
          this.userPassword = progress.password || "";
          this.userPhone = progress.phone || "";
          this.userDetails = progress.userDetails || null;
          this.phoneVerified = progress.phoneVerified || false;
        } catch (e) {
          console.error("Error restoring progress:", e);
        }
      } else {
        this.goToStep(1);
      }
    },

    clearProgress() {
      localStorage.removeItem("login_progress");
    },

    // Query parameter handling
    handleQueryParams() {
      const query = this.$route.query;

      if (query["change-phone-code"]) {
        this.goToStep(4);
        // Validate the code here if needed
      }

      if (query.mid) {
        this.getMemberOrRegisterMember();
      }
    },

    // Step 1: Email submission
    async handleEmailSubmit() {
      if (!this.emailValid) {
        this.showError("Invalid Email Address!");
        return;
      }

      this.$store.commit("setLoader", true);
      try {
        const response = await axios.postData("/auth/get-user-details", {
          email: this.userEmail,
        });
        //console.log(response)
        if (response && response.status === "not-exist") {
          this.showError(
            "User not found. Please check your email address or REGISTER."
          );
          this.$router.push({ path: "/register" });
        } else if (response && response.status === "no-emis") {
          this.goToStep(5);
          this.addNewCompanyDialog = true;
        } else if (response && response.status === "not-authorised") {
          this.showError(
            "User Not Authorised. Contact Admin to give you access."
          );
        } else if (response && response.status === "success") {
          this.userDetails = response.user;
          this.phoneVerified = response.verified;
          this.userPhone = response.user.phone_number || "";

          this.goToStep(2);

          if (this.phoneVerified) {
            this.startResendTimer();
          }
        } else {
          this.showError(
            "User not found. Please check your email address or REGISTER."
          );
        }
      } catch (error) {
        console.log("error", error);
        this.showError("Failed to verify email. Please try again.");
      } finally {
        await this.getSMSCreditsNoToken();
      }
      this.$store.commit("setLoader", false);
    },

    // Step 2: Phone number update
    async updatePhoneNumber() {
      if (!this.phoneValid) return;

      this.loading = true;
      try {
        const response = await axios.postData("/auth/update-phone", {
          email: this.userEmail,
          phone: this.userPhone,
          password: this.userPassword,
        });

        if (response.status === "success") {
          this.phoneVerified = true;
          this.startResendTimer();
          this.$toast.success("Phone number updated! OTP sent to your phone.");
        } else {
          this.showError("Failed to update phone number. Please try again.");
        }
      } catch (error) {
        this.showError("Failed to update phone number. Please try again.");
      }
      this.loading = false;
    },

    // Step 2: OTP submission
    async handleOTPSubmit() {
      if (this.otpCode.length !== 6) return;

      this.loading = true;
      try {
        const response = await axios.postData("/auth/login", {
          email: this.userEmail,
          password: this.otpCode,
          mode: "otp",
        });

        if (response.status === "success") {
          await this.handleSuccessfulLogin(response);
        } else {
          this.showError("Invalid OTP. Please try again.");
          this.otpCode = "";
        }
      } catch (error) {
        this.showError("Login failed. Please try again.");
        this.otpCode = "";
      }
      this.loading = false;
    },

    // Step 3: Request phone change
    async requestPhoneChange() {
      if (!this.changeEmailValid) return;

      this.loading = true;
      try {
        const response = await axios.postData("/auth/change-phone-number", {
          email: this.changeEmail,
        });

        if (response.status === "success") {
          this.$toast.success("Verification link sent to your email!");
          this.goToStep(1);
        } else {
          this.showError("Email not found. Please check your email address.");
        }
      } catch (error) {
        this.showError("Failed to send verification link. Please try again.");
      }
      this.loading = false;
    },

    // Step 4: Save new phone number
    async saveNewPhoneNumber() {
      if (!this.newPhoneValid) return;

      this.loading = true;
      this.userEmail = this.$route.query["email"];

      try {
        const response = await axios.postData("/auth/save-new-phone-number", {
          email: this.userEmail,
          phone: this.newPhoneNumber,
          code: this.$route.query["change-phone-code"],
        });

        if (response.status === "success") {
          this.userPhone = this.newPhoneNumber;
          this.phoneVerified = true;
          this.goToStep(2);

          this.$toast.success("Phone number updated!");
        } else {
          this.showError("Failed to save phone number. Please try again.");
        }
      } catch (error) {
        this.showError("Failed to save phone number. Please try again.");
      }
      this.loading = false;
    },

    // Resend OTP
    async resendOTP() {
      if (this.resendTimer > 0) return;

      this.loading = true;

      try {
        const response = await axios.postData("/auth/resend-code", {
          email: this.userEmail,
        });

        this.startResendTimer();
        this.$toast.success("New OTP sent to your phone!");
      } catch (error) {
        this.showError("Failed to resend OTP. Please try again.");
      }
      this.loading = false;
    },

    // Timer for resend button
    startResendTimer() {
      this.resendTimer = 1200;
      const timer = setInterval(() => {
        this.resendTimer--;
        if (this.resendTimer <= 0) {
          clearInterval(timer);
        }
      }, 1000);
    },

    // Success handling
    async handleSuccessfulLogin(response) {
      this.showSuccessOverlay = true;

      // Store auth data
      if (response.token && response.user) {
        helpersJs.initialiseOrUpdateCompanyDataInStorage(
          response.user,
          response.company
        );
        this.$store.commit("setAccessToken", response.token);
        this.$store.commit("setAuthenticated", true);
      }

      this.clearProgress();

      // Delay for animation
      setTimeout(() => {
        this.$toast.success("Login Successful, Welcome!", { duration: 1000 });
        //this.$emit("login")
        window.location.reload();
      }, 2000);
    },

    // NFC login (existing functionality)
    async getMemberOrRegisterMember() {
      const queryObj = this.$route.query;
      this.loading = true;

      try {
        const response = await axios.postData("/nfc-login", {
          mid: queryObj.mid,
        });

        if (response.status === "success") {
          await this.handleSuccessfulLogin(response);
        } else {
          this.showError("Unauthorized Access Denied!");
        }
      } catch (error) {
        this.showError("Access denied!");
      }
      this.loading = false;
    },

    // Utility methods
    initiatePhoneChange() {
      this.goToStep(3);
    },

    showError(message) {
      this.$toast.error(message, { duration: 5000 });
    },

    routeTo(pathName) {
      this.clearProgress();
      this.$router.push({ name: pathName });
    },
  },
};
</script>

<style scoped>
/* .login-container {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  /* background: linear-gradient(135deg, #ffffff 0%, #ffffff 100%); */
/* padding: 20px;
} */
.login-container {
  /* 1. Allows the container to grow naturally past 100vh if content is long */
  min-height: 100vh;
  height: auto;
  /* 2. Flex properties */
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  /* 3. Forces the page/parent wrapper to handle the scrolling when content overflows */
  overflow-y: auto;
}

.login-card {
  width: 100%;
  max-width: 450px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.progress-container {
  position: relative;
  top: 0;
}

.step-content {
  min-height: 200px;
}

.custom-input {
  margin-bottom: 16px;
}

.custom-input ::v-deep(.v-field) {
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 12px;
}

.custom-input ::v-deep(.v-field__input) {
  color: white;
}

.custom-input ::v-deep(.v-label) {
  color: rgba(255, 255, 255, 0.8);
}

.custom-input ::v-deep(.v-field__prepend-inner .v-icon) {
  color: rgba(255, 255, 255, 0.8);
}

.custom-btn {
  background: rgba(255, 255, 255, 0.2) !important;
  color: white !important;
  border-radius: 12px;
  font-weight: 600;
  text-transform: none;
  backdrop-filter: blur(10px);
}

.custom-btn:hover {
  background: rgba(255, 255, 255, 0.3) !important;
}

.custom-otp {
  display: flex;
  justify-content: center;
  gap: 8px;
}

.custom-otp ::v-deep(.v-otp-input__content) {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: white;
}

/* Transitions */
.slide-left-enter-active,
.slide-left-leave-active,
.slide-right-enter-active,
.slide-right-leave-active {
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.slide-left-enter-from {
  opacity: 0;
  transform: translateX(50px);
}

.slide-left-leave-to {
  opacity: 0;
  transform: translateX(-50px);
}

.slide-right-enter-from {
  opacity: 0;
  transform: translateX(-50px);
}

.slide-right-leave-to {
  opacity: 0;
  transform: translateX(50px);
}

/* Animations */
@keyframes bounce {
  0%,
  20%,
  50%,
  80%,
  100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-10px);
  }
  60% {
    transform: translateY(-5px);
  }
}

.animate-bounce {
  animation: bounce 1s ease-in-out;
}

/* Responsive design */
@media (max-width: 600px) {
  .login-container {
    padding: 10px;
  }

  .login-card {
    max-width: 100%;
  }
}
</style>
